import type { WrpfRecordCategory, WrpfRecordJurisdiction } from "../handoff/types";
import type { CSSProperties } from "react";

const REGIONAL_JURISDICTIONS: ReadonlyArray<WrpfRecordJurisdiction> = ["NOA", "NEA", "CUYO", "PAMPEANA", "PATAGONICA"];

export const WRPF_RECORD_CATEGORY_PRIORITY: ReadonlyArray<WrpfRecordCategory> = [
  "REGIONAL",
  "NATIONAL",
  "IBEROAMERICAN",
  "WORLD",
];

export const isWrpfRecordCategory = (value: unknown): value is WrpfRecordCategory =>
  typeof value === "string" && WRPF_RECORD_CATEGORY_PRIORITY.includes(value as WrpfRecordCategory);

export type RecordCategoryPresentation = {
  readonly label: string;
  readonly gradientStart: string;
  readonly gradientEnd: string;
  readonly textColor: string;
};

const RECORD_CATEGORY_PRESENTATION: Readonly<Record<WrpfRecordCategory, RecordCategoryPresentation>> = {
  REGIONAL: {
    label: "RÉCORD REGIONAL",
    gradientStart: "#A90528",
    gradientEnd: "#C30A33",
    textColor: "#FFFFFF",
  },
  NATIONAL: {
    label: "RÉCORD NACIONAL",
    gradientStart: "#61A2CA",
    gradientEnd: "#88C3E1",
    textColor: "#15191D",
  },
  IBEROAMERICAN: {
    label: "RÉCORD IBEROAMERICANO",
    gradientStart: "#CED7DE",
    gradientEnd: "#E4E9ED",
    textColor: "#15191D",
  },
  WORLD: {
    label: "RÉCORD MUNDIAL",
    gradientStart: "#DE9C10",
    gradientEnd: "#FCCD62",
    textColor: "#15191D",
  },
};

export const recordCategoryFromJurisdiction = (
  jurisdiction: WrpfRecordJurisdiction,
): WrpfRecordCategory | undefined => {
  if (REGIONAL_JURISDICTIONS.includes(jurisdiction)) return "REGIONAL";
  if (jurisdiction === "NATIONAL" || jurisdiction === "IBEROAMERICAN" || jurisdiction === "WORLD") {
    return jurisdiction;
  }
  return undefined;
};

export const highestRecordCategory = (categories: ReadonlyArray<WrpfRecordCategory>): WrpfRecordCategory | undefined =>
  categories.reduce<WrpfRecordCategory | undefined>((highest, category) => {
    if (highest === undefined) return category;
    return WRPF_RECORD_CATEGORY_PRIORITY.indexOf(category) > WRPF_RECORD_CATEGORY_PRIORITY.indexOf(highest)
      ? category
      : highest;
  }, undefined);

export const getRecordCategoryPresentation = (category: WrpfRecordCategory): RecordCategoryPresentation =>
  RECORD_CATEGORY_PRESENTATION[category];

export const recordAttemptLabel = (category: WrpfRecordCategory | undefined): string =>
  category === undefined ? "RÉCORD" : getRecordCategoryPresentation(category).label;

export const recordAttemptAriaLabel = (category: WrpfRecordCategory | undefined): string =>
  category === undefined ? "Intento récord" : recordAttemptLabel(category);

export type RecordCategoryCssProperties = CSSProperties & {
  readonly "--wrpf-record-gradient-start"?: string;
  readonly "--wrpf-record-gradient-end"?: string;
  readonly "--wrpf-record-text-color"?: string;
};

export type RecordCategoryDataAttributes = {
  readonly "data-record-category"?: WrpfRecordCategory;
  readonly style?: RecordCategoryCssProperties;
};

export const recordCategoryDataAttributes = (
  category: WrpfRecordCategory | undefined,
): RecordCategoryDataAttributes => {
  if (category === undefined) return {};
  const presentation = getRecordCategoryPresentation(category);
  return {
    "data-record-category": category,
    style: {
      "--wrpf-record-gradient-start": presentation.gradientStart,
      "--wrpf-record-gradient-end": presentation.gradientEnd,
      "--wrpf-record-text-color": presentation.textColor,
    },
  };
};
